import React, { useState, useEffect, useRef } from 'react';
import './Sidebar.css';
import takkuLogo from './assets/takku-logo.png';

interface Session {
    id: string;
    title: string;
    updated_at: string;
    is_pinned?: boolean;
}

interface SidebarProps {
    sessions: Session[];
    currentSessionId: string | null;
    onSelectSession: (sessionId: string) => void;
    onNewChat: () => void;
    onDeleteSession: (sessionId: string) => void;
    onRenameSession: (sessionId: string, newTitle: string) => void;
    onPinSession: (sessionId: string) => void;
    onShareSession: (sessionId: string) => void;
    isOpen: boolean;
    toggleSidebar: () => void;
    currentUser?: { name?: string; email?: string } | null;
    onOpenSettings?: () => void;
    onLogout?: () => void;
    onOpenMemory?: () => void;
    onOpenReminders?: () => void;
    onOpenMyDocs?: () => void;
    onAskTakkuToCall?: () => void;
}

// Group sessions by date
const groupSessionsByDate = (sessions: Session[]): { [key: string]: Session[] } => {
    const groups: { [key: string]: Session[] } = {};
    const now = new Date();

    sessions.forEach(session => {
        const date = new Date(session.updated_at);
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        let groupKey: string;
        if (diffDays === 0) {
            groupKey = 'Today';
        } else if (diffDays === 1) {
            groupKey = 'Yesterday';
        } else if (diffDays < 7) {
            groupKey = 'This Week';
        } else if (diffDays < 30) {
            groupKey = 'This Month';
        } else {
            groupKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        }

        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }
        groups[groupKey].push(session);
    });

    return groups;
};

const Sidebar: React.FC<SidebarProps> = ({
    sessions,
    currentSessionId,
    onSelectSession,
    onNewChat,
    onDeleteSession,
    onRenameSession,
    onPinSession,
    onShareSession,
    isOpen,
    toggleSidebar,
    currentUser,
    onOpenSettings,
    onLogout,
    onOpenMemory,
    onOpenReminders,
    onOpenMyDocs,
    onAskTakkuToCall
}) => {
    const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [libraryExpanded, setLibraryExpanded] = useState(false);
    const [vibeExpanded, setVibeExpanded] = useState(false);
    const [showTakkuPhone, setShowTakkuPhone] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuOpenId && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpenId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpenId]);

    const handleMenuClick = (e: React.MouseEvent, sessionId: string) => {
        e.stopPropagation();
        setMenuOpenId(menuOpenId === sessionId ? null : sessionId);
    };

    const handleRenameStart = (e: React.MouseEvent, session: Session) => {
        e.stopPropagation();
        setEditingId(session.id);
        setEditTitle(session.title || '');
        setMenuOpenId(null);
    };

    const handleRenameSubmit = (sessionId: string) => {
        if (editTitle.trim()) {
            onRenameSession(sessionId, editTitle.trim());
        }
        setEditingId(null);
        setEditTitle('');
    };

    const handleDeleteClick = (e: React.MouseEvent, sessionId: string) => {
        e.stopPropagation();
        setDeleteConfirmId(sessionId);
        setMenuOpenId(null);
    };

    const handleDeleteConfirm = () => {
        if (deleteConfirmId) {
            onDeleteSession(deleteConfirmId);
            setDeleteConfirmId(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmId(null);
    };

    const handlePin = (e: React.MouseEvent, sessionId: string) => {
        e.stopPropagation();
        onPinSession(sessionId);
        setMenuOpenId(null);
    };

    const handleShare = (e: React.MouseEvent, sessionId: string) => {
        e.stopPropagation();
        onShareSession(sessionId);
        setMenuOpenId(null);
    };

    // Group sessions by date
    const groupedSessions = groupSessionsByDate(sessions);
    const dateOrder = ['Today', 'Yesterday', 'This Week', 'This Month'];

    return (
        <>
            {/* Mobile Overlay */}
            <div
                className={`sidebar-overlay ${isOpen ? 'open' : ''}`}
                onClick={toggleSidebar}
            />

            {/* Delete Confirmation Modal */}
            {deleteConfirmId && (
                <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
                    <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-modal-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                        </div>
                        <h3>Delete Chat?</h3>
                        <p>This action cannot be undone. All messages in this conversation will be permanently deleted.</p>
                        <div className="delete-modal-actions">
                            <button className="cancel-btn" onClick={handleDeleteCancel}>
                                Cancel
                            </button>
                            <button className="confirm-delete-btn" onClick={handleDeleteConfirm}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar Container */}
            <div className={`sidebar ${isOpen ? 'open' : ''}`}>
                {/* Header: Logo + New Chat Icon */}
                <div className="sidebar-header">
                    <div className="sidebar-brand">
                        <img src={takkuLogo} alt="Takku" className="sidebar-logo" />
                        <span className="sidebar-title">Takku</span>
                    </div>
                    <button
                        className="new-chat-icon"
                        onClick={onNewChat}
                        aria-label="New chat"
                        title="New chat"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"/>
                            <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                    </button>
                </div>

                {/* Navigation: Settings + Library */}
                <div className="sidebar-nav">
                    {onOpenSettings && (
                        <button className="nav-item" onClick={onOpenSettings}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="3"/>
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                            </svg>
                            <span>Settings</span>
                        </button>
                    )}

                    {/* Library Folder */}
                    <div className="nav-folder">
                        <button
                            className={`nav-item folder-toggle ${libraryExpanded ? 'expanded' : ''}`}
                            onClick={() => setLibraryExpanded(!libraryExpanded)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                            </svg>
                            <span>Library</span>
                            <svg className="folder-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </button>

                        {libraryExpanded && (
                            <div className="nav-folder-contents">
                                {onOpenMemory && (
                                    <button className="nav-item nested" onClick={onOpenMemory}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <path d="M12 6v6l4 2"/>
                                        </svg>
                                        <span>Memory</span>
                                    </button>
                                )}
                                {onOpenReminders && (
                                    <button className="nav-item nested" onClick={onOpenReminders}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                        </svg>
                                        <span>Reminders</span>
                                    </button>
                                )}
                                {onOpenMyDocs && (
                                    <button className="nav-item nested" onClick={onOpenMyDocs}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                        </svg>
                                        <span>My Docs</span>
                                    </button>
                                )}

                                {/* Vibe with Takku sub-folder — only for logged-in users */}
                                {currentUser && <div className="nav-sub-folder">
                                    <button
                                        className={`nav-item nested folder-toggle ${vibeExpanded ? 'expanded' : ''}`}
                                        onClick={() => setVibeExpanded(!vibeExpanded)}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                        <span>Vibe with Takku</span>
                                        <svg className="folder-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="9 18 15 12 9 6"/>
                                        </svg>
                                    </button>

                                    {vibeExpanded && (
                                        <div className="nav-sub-folder-contents">
                                            <button
                                                className="nav-item nested"
                                                onClick={() => setShowTakkuPhone(!showTakkuPhone)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94"/>
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72"/>
                                                </svg>
                                                <span>Talk to Takku</span>
                                            </button>
                                            {showTakkuPhone && (
                                                <div className="takku-phone-display">
                                                    <a href="tel:+14783713799">+1 (478) 371-3799</a>
                                                </div>
                                            )}

                                            {onAskTakkuToCall && (
                                                <button
                                                    className="nav-item nested"
                                                    onClick={onAskTakkuToCall}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                                        <line x1="12" y1="1" x2="12" y2="7"/>
                                                        <polyline points="16 4 12 1 8 4"/>
                                                    </svg>
                                                    <span>Ask Takku to Call</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sessions List - Grouped by Date */}
                <div className="sessions-list">
                    {sessions.length === 0 ? (
                        <div className="no-sessions">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                            <p>No conversations yet</p>
                        </div>
                    ) : (
                        <>
                            {/* Pinned Sessions First */}
                            {sessions.filter(s => s.is_pinned).length > 0 && (
                                <div className="session-group">
                                    <div className="session-group-header">Pinned</div>
                                    {sessions.filter(s => s.is_pinned).map(session => (
                                        <SessionItem
                                            key={session.id}
                                            session={session}
                                            isActive={currentSessionId === session.id}
                                            isEditing={editingId === session.id}
                                            editTitle={editTitle}
                                            menuOpenId={menuOpenId}
                                            menuRef={menuRef}
                                            onSelect={() => {
                                                onSelectSession(session.id);
                                                if (window.innerWidth <= 768) toggleSidebar();
                                            }}
                                            onMenuClick={handleMenuClick}
                                            onRenameStart={handleRenameStart}
                                            onRenameSubmit={handleRenameSubmit}
                                            onDeleteClick={handleDeleteClick}
                                            onPin={handlePin}
                                            onShare={handleShare}
                                            setEditTitle={setEditTitle}
                                            setEditingId={setEditingId}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Date-grouped Sessions */}
                            {[...dateOrder, ...Object.keys(groupedSessions).filter(k => !dateOrder.includes(k))].map(dateKey => {
                                const sessionsInGroup = groupedSessions[dateKey]?.filter(s => !s.is_pinned);
                                if (!sessionsInGroup || sessionsInGroup.length === 0) return null;

                                return (
                                    <div key={dateKey} className="session-group">
                                        <div className="session-group-header">{dateKey}</div>
                                        {sessionsInGroup.map(session => (
                                            <SessionItem
                                                key={session.id}
                                                session={session}
                                                isActive={currentSessionId === session.id}
                                                isEditing={editingId === session.id}
                                                editTitle={editTitle}
                                                menuOpenId={menuOpenId}
                                                menuRef={menuRef}
                                                onSelect={() => {
                                                    onSelectSession(session.id);
                                                    if (window.innerWidth <= 768) toggleSidebar();
                                                }}
                                                onMenuClick={handleMenuClick}
                                                onRenameStart={handleRenameStart}
                                                onRenameSubmit={handleRenameSubmit}
                                                onDeleteClick={handleDeleteClick}
                                                onPin={handlePin}
                                                onShare={handleShare}
                                                setEditTitle={setEditTitle}
                                                setEditingId={setEditingId}
                                            />
                                        ))}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Footer: User + New Button */}
                <div className="sidebar-footer">
                    <div className="footer-content">
                        <div className="user-profile-compact">
                            <div className="user-avatar">
                                {currentUser?.name?.charAt(0).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || 'T'}
                            </div>
                            <span className="user-name-compact">
                                {currentUser?.name || currentUser?.email?.split('@')[0] || 'User'}
                            </span>
                        </div>


                    </div>
                </div>
            </div>
        </>
    );
};

// Session Item Component
interface SessionItemProps {
    session: Session;
    isActive: boolean;
    isEditing: boolean;
    editTitle: string;
    menuOpenId: string | null;
    menuRef: React.RefObject<HTMLDivElement | null>;
    onSelect: () => void;
    onMenuClick: (e: React.MouseEvent, sessionId: string) => void;
    onRenameStart: (e: React.MouseEvent, session: Session) => void;
    onRenameSubmit: (sessionId: string) => void;
    onDeleteClick: (e: React.MouseEvent, sessionId: string) => void;
    onPin: (e: React.MouseEvent, sessionId: string) => void;
    onShare: (e: React.MouseEvent, sessionId: string) => void;
    setEditTitle: (title: string) => void;
    setEditingId: (id: string | null) => void;
}

const SessionItem: React.FC<SessionItemProps> = ({
    session,
    isActive,
    isEditing,
    editTitle,
    menuOpenId,
    menuRef,
    onSelect,
    onMenuClick,
    onRenameStart,
    onRenameSubmit,
    onDeleteClick,
    onPin,
    onShare,
    setEditTitle,
    setEditingId
}) => {
    return (
        <div
            className={`session-item ${isActive ? 'active' : ''} ${session.is_pinned ? 'pinned' : ''}`}
            onClick={onSelect}
        >
            <div className="session-content">
                {isEditing ? (
                    <input
                        type="text"
                        className="session-edit-input"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onBlur={() => onRenameSubmit(session.id)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onRenameSubmit(session.id);
                            if (e.key === 'Escape') { setEditingId(null); setEditTitle(''); }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                    />
                ) : (
                    <span className="session-title">{session.title || "New Chat"}</span>
                )}
            </div>

            <button
                className="session-menu-btn"
                onClick={(e) => onMenuClick(e, session.id)}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="5" r="1"/>
                    <circle cx="12" cy="12" r="1"/>
                    <circle cx="12" cy="19" r="1"/>
                </svg>
            </button>

            {menuOpenId === session.id && (
                <div className="session-menu" ref={menuRef}>
                    <button onClick={(e) => onRenameStart(e, session)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        Rename
                    </button>
                    <button onClick={(e) => onPin(e, session.id)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 17v5"/>
                            <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3v4.76z"/>
                        </svg>
                        {session.is_pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button onClick={(e) => onShare(e, session.id)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="18" cy="5" r="3"/>
                            <circle cx="6" cy="12" r="3"/>
                            <circle cx="18" cy="19" r="3"/>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                        </svg>
                        Share
                    </button>
                    <button className="delete-btn" onClick={(e) => onDeleteClick(e, session.id)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
};

export default Sidebar;
